import * as React from 'react';
import { Box, Typography } from '@mui/material';
import {
    FlexyFormLabel,
    FlexyTextField,
    Switch,
} from '@europrocurement/flexy-components/components/atoms';
import type { ProcessedAddress } from '@europrocurement/flexy-components/hooks/useAddressLogic';

export type UserStructureAddressDetailsFormProps = {
    address: ProcessedAddress;
    isBillingAddress?: boolean;
    isBillingAddressSame?: boolean;
};

/**
 * Composant de formulaire d'adresse de structure.
 */
export const UserStructureAddressDetailsForm: React.FunctionComponent<UserStructureAddressDetailsFormProps> =
    function ({ address, isBillingAddress = false, isBillingAddressSame = true }) {
        return (
            <Box
                p={2}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography
                    variant="h3"
                    paddingBottom="10px"
                >
                    {isBillingAddress ? 'Adresse de facturation' : 'Adresse'}
                </Typography>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel required>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Numéro, rue
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.numeroRue}
                        placeholder="123 Avenue des Champs-Élysées"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Appartement, bâtiment, entrée
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.appartement}
                        placeholder="Bâtiment A, Entrée 2, Apt 304"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Complément d&apos;adresse
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.complement}
                        placeholder="Résidence Les Jardins"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Boîte postale, CEDEX
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.boitePostale}
                        placeholder="BP 12345, 75008 PARIS CEDEX 08"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel required>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Code postal
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.codePostal}
                        placeholder="75008"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel required>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Ville
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.ville}
                        placeholder="Paris"
                        disabled
                    />
                </Box>

                <Box sx={{ gap: '4px', display: 'grid' }}>
                    <FlexyFormLabel>
                        <Typography
                            variant="l2dbodys"
                            fontWeight={500}
                        >
                            Pays
                        </Typography>
                    </FlexyFormLabel>
                    <FlexyTextField
                        type="text"
                        variant="outlined"
                        fullWidth
                        value={address.pays}
                        placeholder="FR"
                        disabled
                    />
                </Box>

                {!isBillingAddress && (
                    <>
                        <Box sx={{ height: 16 }} />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="l2dbodys">
                                Adresse de facturation identique
                            </Typography>
                            <Switch
                                checked={isBillingAddressSame}
                                disabled
                            />
                        </Box>
                    </>
                )}
            </Box>
        );
    };
